import {useCallback, useEffect, useLayoutEffect, useState, type RefObject} from 'react'

const MIN_TILE_WIDTH = 200
const TILE_ASPECT = 16 / 9
const STRIP_MIN_TILE_WIDTH = 160
const GAP = 8

interface GridLayout {
  cols: number
  rows: number
  itemsPerPage: number
}

/** Prefer horizontal (side-by-side) layout for 2 participants on wide screens. */
function prefersHorizontalPair(width: number, height: number): boolean {
  return width > height
}

function computeOptimalGrid(
  count: number,
  width: number,
  height: number,
): {cols: number; rows: number; itemsPerPage: number} {
  if (count <= 0) return {cols: 1, rows: 1, itemsPerPage: 1}

  if (count === 2 && prefersHorizontalPair(width, height)) {
    return {cols: 2, rows: 1, itemsPerPage: 2}
  }

  let bestCols = 1
  let bestRows = 1
  let maxTileWidth = 0

  for (let cols = 1; cols <= count; cols += 1) {
    const rows = Math.ceil(count / cols)
    const availableWidth = width - GAP * (cols - 1)
    const availableHeight = height - GAP * (rows - 1)
    const tileWidthFromWidth = availableWidth / cols
    const tileHeightFromHeight = availableHeight / rows
    const tileWidthFromHeight = tileHeightFromHeight * TILE_ASPECT
    const tileWidth = Math.min(tileWidthFromWidth, tileWidthFromHeight)
    if (tileWidth > maxTileWidth) {
      maxTileWidth = tileWidth
      bestCols = cols
      bestRows = rows
    }
  }

  return {cols: bestCols, rows: bestRows, itemsPerPage: bestCols * bestRows}
}

export function useGridLayout(
  containerRef: RefObject<HTMLDivElement | null>,
  mode: 'grid' | 'focus',
  trackCount: number,
): GridLayout {
  const [layout, setLayout] = useState<GridLayout>({cols: 1, rows: 1, itemsPerPage: 1})

  const compute = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const {width, height} = el.getBoundingClientRect()
    if (width === 0 || height === 0) return

    if (mode === 'grid') {
      const maxCols = Math.max(1, Math.floor((width + GAP) / (MIN_TILE_WIDTH + GAP)))
      const tileWidth = (width - GAP * (maxCols - 1)) / maxCols
      const tileHeight = tileWidth / TILE_ASPECT
      const maxRows = Math.max(1, Math.floor((height + GAP) / (tileHeight + GAP)))
      const maxCapacity = maxCols * maxRows

      if (trackCount <= maxCapacity) {
        setLayout(computeOptimalGrid(trackCount, width, height))
      } else {
        setLayout({cols: maxCols, rows: maxRows, itemsPerPage: maxCapacity})
      }
    } else {
      const cols = Math.max(1, Math.floor((width + GAP) / (STRIP_MIN_TILE_WIDTH + GAP)))
      setLayout({cols, rows: 1, itemsPerPage: cols})
    }
  }, [containerRef, mode, trackCount])

  useLayoutEffect(() => {
    compute()
  }, [compute])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver(compute)
    observer.observe(el)
    return () => observer.disconnect()
  }, [containerRef, compute])

  return layout
}
